import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FlightItem, TravelClass } from '../../../../../types/flight.types';
import { useSaveFlightToIdeaMutation } from '../../../../../store/services/ideasApi';
import { useSaveFlightToTripMutation } from '../../../../../store/services/tripsApi';
import { buildFlightToIdeaPayload, FlightSearchContextForSave } from '../../../../../utils/flightToIdeaPayload';
import { getApiErrorMessage } from '../../../../../utils/apiErrorMessage';
import {
  PENDING_FLIGHT_TO_IDEA_STORAGE_KEY,
  PENDING_FLIGHT_TO_TRIP_STORAGE_KEY,
} from '../../../../../constants/discoverIdeaFlow';
import ChooseWhereToSaveModal, {
  type ChooseWhereToSavePayload,
} from '../ChooseWhereToSaveModal/ChooseWhereToSaveModal';

export interface SaveFlightModalProps {
  open: boolean;
  onClose: () => void;
  flight: FlightItem | null;
  searchContext: FlightSearchContextForSave;
  travelersCount: number;
  travelClass: TravelClass;
  onSaved?: (message: string) => void;
}

const SaveFlightModal: FC<SaveFlightModalProps> = ({
  open,
  onClose,
  flight,
  searchContext,
  travelersCount,
  travelClass,
  onSaved,
}) => {
  const [saveError, setSaveError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [saveFlightToIdea, { isLoading: isSavingToIdea }] = useSaveFlightToIdeaMutation();
  const [saveFlightToTrip, { isLoading: isSavingToTrip }] = useSaveFlightToTripMutation();
  const isSaving = isSavingToIdea || isSavingToTrip;

  useEffect(() => {
    if (!open) {
      setSaveError(null);
    }
  }, [open]);

  const handleCreateNewIdeaList = () => {
    if (!flight) return;
    const body = buildFlightToIdeaPayload(flight, travelersCount, travelClass, searchContext);
    try {
      sessionStorage.setItem(PENDING_FLIGHT_TO_IDEA_STORAGE_KEY, JSON.stringify({ body }));
    } catch {
      return;
    }
    onClose();
    navigate('/ideas?fromDiscover=1');
  };

  const handleCreateNewTrip = () => {
    if (!flight) return;
    const body = buildFlightToIdeaPayload(flight, travelersCount, travelClass, searchContext);
    try {
      sessionStorage.setItem(PENDING_FLIGHT_TO_TRIP_STORAGE_KEY, JSON.stringify({ body }));
    } catch {
      return;
    }
    onClose();
    navigate('/trips?fromDiscover=1');
  };
  const handleSave = async ({ tab, ideaId, tripId }: ChooseWhereToSavePayload) => {
    setSaveError(null);
    if (!flight) return;

    const body = buildFlightToIdeaPayload(flight, travelersCount, travelClass, searchContext);

    if (tab === 'ideas') {
      if (ideaId == null) {
        setSaveError('Select an ideas list to save this flight.');
        return;
      }
      try {
        const res = await saveFlightToIdea({ ideaId, body }).unwrap();
        const msg = res.message || `Saved to ${res.idea_list_name || 'your list'}.`;
        onSaved?.(msg);
        onClose();
      } catch (e: unknown) {
        setSaveError(getApiErrorMessage(e, 'Could not save flight. Try again.', { firstOnly: true }));
      }
      return;
    }

    if (tripId == null) {
      setSaveError('Select a trip to save this flight.');
      return;
    }
    try {
      const res = await saveFlightToTrip({ tripId, body }).unwrap();
      const msg = res.message || `Saved to ${res.trip_name || 'your trip'}.`;
      onSaved?.(msg);
      onClose();
    } catch (e: unknown) {
      setSaveError(
        getApiErrorMessage(e, 'Could not save flight to this trip. Try again.', { firstOnly: true })
      );
    }
  };

  return (
    <ChooseWhereToSaveModal
      open={open}
      onClose={onClose}
      titleId="save-flight-modal-title"
      searchInputName="save-flight-search"
      onSave={handleSave}
      isSaving={isSaving}
      disableFooterWhileSaving
      noIdeaListsEmptyMessage="You have no idea lists yet. Create one below."
      onCreateNewIdeaList={handleCreateNewIdeaList}
      onCreateNewTrip={handleCreateNewTrip}
      createNewIdeaListDisabled={!flight}
      errorText={saveError}
      onErrorDismiss={() => setSaveError(null)}
    />
  );
};

export default SaveFlightModal;
